import { isNotStarted, isInProgress, isCompleted } from './job';

import {
  format,
  parseISO,
  formatDistanceToNow as _formatDistanceToNow,
  startOfDay,
  startOfWeek,
  startOfMonth,
  startOfHour,
  endOfDay,
  endOfWeek,
  endOfMonth,
  endOfHour,
  differenceInSeconds,
  differenceInHours,
  addSeconds,
  addHours,
  addDays,
  addWeeks,
  addMonths,
  subSeconds,
  addMinutes,
  startOfSecond,
  startOfMinute,
  endOfMinute,
  endOfSecond,
  subHours,
  subDays,
  subWeeks,
  subMonths,
  subMinutes
} from 'date-fns';

export const formats = {
  date: 'MM/dd/yy',
  time: 'h:mm a',
  dateTime: 'MM/dd/yy - h:mm a',
  shortDate: 'MMM d, h:mm a'
};

export const getTime = (date) => {
  return new Date(date).getTime();
};

const convertString = (dateOrString) =>
  typeof dateOrString === 'string'
    ? new Date(parseISO(dateOrString))
    : new Date(dateOrString);

export const formatDate = (dateOrString, dateFormat = 'date') => {
  return format(convertString(dateOrString), formats[dateFormat] || dateFormat);
};

export const formatDistanceToNow = (
  dateOrString,
  options = { addSuffix: true }
) => {
  return _formatDistanceToNow(convertString(dateOrString), options);
};

const startOfFuncs = {
  second: startOfSecond,
  minute: startOfMinute,
  hour: startOfHour,
  day: startOfDay,
  week: startOfWeek,
  month: startOfMonth
};

export const startOf = (range, dateOrString = new Date()) => {
  return startOfFuncs[range](convertString(dateOrString));
};

const endOfFuncs = {
  second: endOfSecond,
  minute: endOfMinute,
  hour: endOfHour,
  day: endOfDay,
  week: endOfWeek,
  month: endOfMonth
};

export const endOf = (range, dateOrString = new Date()) => {
  return endOfFuncs[range](convertString(dateOrString));
};

const diffFuncs = {
  seconds: differenceInSeconds,
  hours: differenceInHours
};

export const diff = (start, end = new Date(), format = 'seconds') => {
  return diffFuncs[format](convertString(start), convertString(end));
};

const addFuncs = {
  seconds: addSeconds,
  minutes: addMinutes,
  hours: addHours,
  days: addDays,
  weeks: addWeeks,
  months: addMonths
};

export const add = (dateOrString, value, type = 'seconds') => {
  return addFuncs[type](convertString(dateOrString), value);
};

const subtractFuncs = {
  seconds: subSeconds,
  minutes: subMinutes,
  hours: subHours,
  days: subDays,
  weeks: subWeeks,
  months: subMonths
};

export const subtract = (dateOrString, value, type = 'seconds') => {
  return subtractFuncs[type](convertString(dateOrString), value);
};

export const convert = (value, to, from = 'seconds') => {
  if (from === 'seconds') {
    if (to === 'hours') {
      return Number(value) / 3600;
    }
    if (to === 'minutes') {
      return Number(value) / 60;
    }
  }

  if (from === 'hours') {
    if (to === 'seconds') {
      return Number(value) * 3600;
    }
    if (to === 'minutes') {
      return Number(value) * 60;
    }
  }

  if (from === 'minutes') {
    if (to === 'seconds') {
      return Number(value) * 60;
    }
    if (to === 'hours') {
      return Number(value) / 60;
    }
  }
};

const getString = (value) => {
  let string;
  if (value === 0) {
    string = '00';
  } else if (value < 10) {
    string = `0` + value;
  } else {
    string = value;
  }
  return string;
};

export const secondsToTime = (secs) => {
  const rawHours = secs / 60 / 60;
  const hours = Math.floor(rawHours);

  const rawMinutes = (rawHours - hours) * 60;
  const minutes = Math.floor(rawMinutes);

  const rawSeconds = (rawMinutes - minutes) * 60;
  const seconds = Math.round(rawSeconds);

  const hourString = getString(hours);
  const minString = getString(minutes);
  const secString = getString(seconds);

  // let string = '';
  // if (format.includes('hrs')) {
  //   string = string + hourString;
  // }
  // if (format.includes('mins')) {
  //   string = `${string}:${minString}`;
  // }
  // if (format.includes('secs')) {
  //   string = `${string}:${secString}`;
  // }
  //
  // return string;

  return `${hourString}:${minString}:${secString}`;
};

export const secondsToText = (seconds) => {
  const time = secondsToTime(seconds);
  const [stringHours, stringMinutes] = time.split(':');
  const hours = Number(stringHours);
  const minutes = Number(stringMinutes);
  let string;
  if (hours > 0) {
    string = `${hours}${hours === 1 ? 'hr' : 'hrs'}`;
    if (minutes > 0) {
      string = string += ` ${minutes}${minutes === 1 ? 'min' : 'mins'}`;
    }
  } else if (minutes > 0) {
    string = `${minutes}${minutes === 1 ? 'min' : 'mins'}`;
  }
  return string;
};

export function earlierDate() {
  let dates = [...arguments];
  dates = dates.filter((date) => date).map((date) => new Date(date));
  dates.sort();
  return dates[0];
}

export function laterDate() {
  let dates = [...arguments];
  dates = dates.filter((date) => date).map((date) => new Date(date));
  dates.sort();
  return dates[dates.length - 1];
}

export const getEstimate = (dateOrString, seconds, format) => {
  const estimate = add(convertString(dateOrString), seconds);
  return format ? formatDate(estimate, format) : estimate.getTime();
};

export const getJobETA = (job, format) => {
  const lastIndex = job.stages.length - 1;
  const lastStage = job.stages[lastIndex];
  const lastStart = getPredictedScheduledStarts(job)[lastIndex];
  return getEstimate(
    lastStart,
    lastStage.adjusted_estimate || lastStage.original_estimate,
    format
  );
};

// Use all original_estimates to get the originally scheduled
// starts for each stage, returned in an array where each entry
// aligns with the index of its stage.
export const getScheduledStarts = (job) => {
  const stages = job.stages;
  return stages.reduce((scheduled_times, stage, index) => {
    const prevIndex = index - 1;
    if (index === 0) {
      // this is the first stage and is scheduled to start at job.scheduled_time
      scheduled_times[index] = getTime(job.scheduled_time);
    } else {
      scheduled_times[index] = getEstimate(
        scheduled_times[prevIndex],
        stages[prevIndex].original_estimate
      );
    }
    return scheduled_times;
  }, []);
};

// This function uses previous' stages actual end times to calculate
// the most "accurate" adjusted start time. But, that is not really the
// adjusted start times. Adjusted start times should combine all of the
// original || adjusted estimates to get "guesstimates" of time.
// This is really `predicted` times because it doesn't just rely on
// adjusted estimates and relies on real times.
export const getPredictedScheduledStarts = (job) => {
  const stages = job.stages;
  return stages.reduce((scheduled_times, stage, index) => {
    const prevIndex = index - 1;
    if (index === 0) {
      scheduled_times[index] = isNotStarted(job)
        ? getTime(job.scheduled_time)
        : getTime(stage.start_time);
    } else if (isCompleted(stage) || isInProgress(stage)) {
      // the stage is complete/active, it has an actual start time
      scheduled_times[index] = getTime(stage.start_time);
    } else {
      // the stage is inactive,
      // each stage's start time is the same as the last stage's end,
      // so use that if possible
      const prevStage = stages[prevIndex];
      const estimate = prevStage.adjusted_estimate
        ? prevStage.adjusted_estimate
        : prevStage.original_estimate;
      const prevStageEnd = prevStage.end_time
        ? prevStage.end_time
        : getEstimate(scheduled_times[prevIndex], estimate);
      scheduled_times[index] = getTime(prevStageEnd);
    }
    return scheduled_times;
  }, []);
};

// This is actual adjusted scheduled starts. It does not rely on
// previous stage end_times and returns what was truly predicted by
// all of the adjusted estimates, without factoring in actual start/ends
export const getAdjustedScheduledStarts = (job) => {
  const stages = job.stages;
  return stages.reduce((scheduled_times, stage, index) => {
    const prevIndex = index - 1;
    if (index === 0) {
      scheduled_times[index] = isNotStarted(job)
        ? getTime(job.scheduled_time)
        : getTime(stage.start_time);
    } else {
      const prevStage = stages[prevIndex];
      const estimate = prevStage.adjusted_estimate
        ? prevStage.adjusted_estimate
        : prevStage.original_estimate;
      scheduled_times[index] = getEstimate(
        scheduled_times[prevIndex],
        estimate
      );
    }
    return scheduled_times;
  }, []);
};
